import type { AuthUserRole } from '../../types/user'
import type { Permission } from '../../types/user'
import type { LoginResponse, ProfileFormState } from '../../types/auth'
import { isNetworkError } from '../../utils/errorUtils'
import { normalizePermissions } from '../../utils/permissions'

export const AUTH_ROLE_STORAGE_KEY = 'pr_auth_role'
export const LEGACY_AUTH_PII_STORAGE_KEYS = [
  AUTH_ROLE_STORAGE_KEY,
  'pr_auth_user_id',
  'pr_auth_user',
  'pr_auth_username',
  'pr_auth_email'
] as const
const AUTH_USER_ROLES: AuthUserRole[] = [
  'Admin',
  'CatalogEditor',
  'ProcurementManager',
  'Head',
  'User',
  'Storekeeper'
]

export function isAuthUserRole(value: unknown): value is AuthUserRole {
  return typeof value === 'string' && AUTH_USER_ROLES.includes(value as AuthUserRole)
}

export function clearLegacyAuthStorage(): void {
  for (const key of LEGACY_AUTH_PII_STORAGE_KEYS) {
    localStorage.removeItem(key)
  }
}

export function createProfileFormState(userName = '', userEmail = ''): ProfileFormState {
  return {
    displayName: userName || '',
    email: userEmail || '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  }
}

export function resolveApiErrorMessage(error: unknown, apiBaseUrl: string, fallbackMessage: string): string {
  if (isNetworkError(error)) {
    return `Не удалось подключиться к API (${apiBaseUrl}). Проверьте, что backend запущен и доступен.`
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallbackMessage
}

export function resolveResetToken(search: string): string {
  const query = new URLSearchParams(search)
  const tokens = query
    .getAll('token')
    .map((value) => value.trim())
    .filter((value) => value.length > 0)

  return tokens.length > 0 ? tokens[tokens.length - 1] : ''
}

export function normalizeAuthUserData(userData: Partial<LoginResponse>, fallbackUserName = ''): {
  userId: number
  userName: string
  username: string
  userEmail: string
  userRole: AuthUserRole
  userPermissions: Permission[]
  mustChangePassword: boolean
} {
  const userId = Number.isInteger(userData?.userId) ? Number(userData.userId) : 0
  const userName = userData?.displayName || userData?.username || fallbackUserName || 'Пользователь'
  const username = userData?.username || fallbackUserName || ''
  const userEmail = userData?.email || ''
  const userRole = isAuthUserRole(userData?.role) ? userData.role : 'User'
  const userPermissions = normalizePermissions(userData?.permissions, userRole)
  const mustChangePassword = Boolean(userData?.requirePasswordChange)

  return {
    userId,
    userName,
    username,
    userEmail,
    userRole,
    userPermissions,
    mustChangePassword
  }
}
